import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { customerBrandStyles } from '../../styles/customer-brand-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { resolveRouterPath, router } from '../../router';
import { ApiFactory } from '../../api/easypos-api';
import { UserProfile } from '../../datatypes/user-profile';
import { setUserContextMode } from '../../state/user-context-mode';
import {
  setPendingJoinCode,
  clearPendingJoinCode,
} from '../../state/pending-join';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

type LoadState =
  | 'loading'
  | 'shop-not-found'
  | 'needs-login'
  | 'enrolling'
  | 'ready'
  | 'already-member'
  | 'joining'
  | 'joined'
  | 'error';

// Mix of palette tones — gold is dominant (primary brand), with a heart
// moment (coral on "gift") and a tropical green for the "favourites" item.
const DEFAULT_BENEFITS = [
  { icon: 'gift-fill', label: 'Earn rewards every visit', tone: 'sun' },
  { icon: 'tag-fill', label: 'Member-only offers + discounts', tone: 'coral' },
  {
    icon: 'star-fill',
    label: 'Save favourites for faster re-orders',
    tone: 'leaf',
  },
];

@customElement('join-shop-page')
export class JoinShopPage extends WithUserProfileConsumer(LitElement) {
  @state() private _state: LoadState = 'loading';
  @state() private _code = '';
  @state() private _shopName: string | null = null;
  @state() private _shopDisplayName: string | null = null;
  @state() private _error: string | null = null;
  private _hasInitialized = false;

  static styles = [
    customerBrandStyles,
    css`
      :host {
        display: block;
      }

      .page {
        padding-bottom: 3rem;
      }
      .blob.a {
        top: -200px;
        right: -160px;
        width: 460px;
        height: 460px;
        background: var(--coral);
        opacity: 0.35;
      }
      .blob.b {
        bottom: -240px;
        left: -180px;
        width: 440px;
        height: 440px;
        background: var(--sun);
        opacity: 0.32;
      }
      .blob.c {
        top: 40%;
        left: 30%;
        width: 280px;
        height: 280px;
        background: var(--mint);
        opacity: 0.18;
      }

      .stage {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 480px;
        margin: 0 auto;
        padding: 0.5rem clamp(1.25rem, 5vw, 2rem) 2rem;
        box-sizing: border-box;
      }

      /* === Hero ============================================ */
      .hero {
        text-align: center;
        padding: 1.5rem 0.25rem 1.75rem;
      }
      .hero .pill {
        margin-bottom: 1.25rem;
      }
      .hero h1 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.9rem, 6.5vw, 2.8rem);
        line-height: 1.05;
        letter-spacing: -0.03em;
        margin: 0 0 0.5rem;
        color: var(--ink);
        overflow-wrap: break-word;
      }
      .hero h1 .shop-name {
        background: linear-gradient(120deg, var(--sun-deep), var(--clay));
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        display: inline;
      }
      .hero p {
        margin: 0 auto;
        max-width: 320px;
        font-size: 1rem;
        font-weight: 500;
        color: var(--ink-soft);
        line-height: 1.5;
      }

      /* === Benefits ======================================== */
      .benefits {
        display: flex;
        flex-direction: column;
        gap: 0.7rem;
        margin: 0 0 1.75rem;
      }
      .benefit {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        padding: 0.9rem 1.1rem;
        background: var(--canvas);
        border: 1px solid var(--line);
        border-radius: 16px;
        box-shadow: var(--shadow-soft);
      }
      .benefit-icon {
        width: 42px;
        height: 42px;
        border-radius: 13px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: white;
        flex: none;
      }
      .benefit-icon.coral {
        background: var(--coral);
        box-shadow: 0 4px 12px var(--coral-glow);
      }
      .benefit-icon.sun {
        background: var(--sun-deep);
        box-shadow: 0 4px 12px var(--sun-glow);
        color: white;
      }
      .benefit-icon.mint {
        background: var(--mint);
        box-shadow: 0 4px 12px var(--mint-glow);
      }
      .benefit-icon.leaf {
        background: var(--leaf);
        box-shadow: 0 4px 12px rgba(74, 140, 90, 0.22);
        color: white;
      }
      .benefit-label {
        font-size: 0.95rem;
        font-weight: 600;
        color: var(--ink);
      }

      /* === Actions ========================================= */
      .actions {
        display: flex;
        flex-direction: column;
        gap: 0.7rem;
      }
      .actions .pa-btn {
        width: 100%;
        padding: 16px 20px;
        font-size: 16px;
      }
      .action-note {
        text-align: center;
        font-size: 0.82rem;
        color: var(--ink-faint);
        font-weight: 500;
        margin: 0;
      }

      /* === Loading / center ================================ */
      .center {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 4rem 1rem;
        color: var(--ink-soft);
        font-size: 0.95rem;
        font-weight: 500;
      }

      /* === Success ========================================== */
      .success {
        text-align: center;
        padding: 2.5rem 1rem 1.5rem;
      }
      .success-mark-wrap {
        position: relative;
        display: inline-block;
        margin-bottom: 1.5rem;
      }
      .success-mark {
        width: 96px;
        height: 96px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--mint), #3fb6ff);
        color: white;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 44px;
        box-shadow: 0 12px 32px var(--mint-glow);
        animation: pop-in 480ms cubic-bezier(0.16, 0.84, 0.44, 1) both;
      }
      @keyframes pop-in {
        from {
          opacity: 0;
          transform: scale(0.5);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      /* Confetti dots */
      .confetti {
        position: absolute;
        inset: -30px;
        pointer-events: none;
      }
      .confetti span {
        position: absolute;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        opacity: 0;
        animation: confetti-burst 1.4s ease-out forwards;
      }
      .confetti span:nth-child(1) {
        background: var(--coral);
        top: 0;
        left: 50%;
        animation-delay: 0.2s;
        --dx: -40px;
        --dy: -50px;
      }
      .confetti span:nth-child(2) {
        background: var(--sun);
        top: 30%;
        right: 0;
        animation-delay: 0.3s;
        --dx: 50px;
        --dy: -20px;
      }
      .confetti span:nth-child(3) {
        background: var(--mint);
        bottom: 0;
        left: 30%;
        animation-delay: 0.4s;
        --dx: -20px;
        --dy: 40px;
      }
      .confetti span:nth-child(4) {
        background: var(--coral);
        top: 50%;
        left: 0;
        animation-delay: 0.25s;
        --dx: -55px;
        --dy: 10px;
      }
      .confetti span:nth-child(5) {
        background: #7b5cff;
        bottom: 20%;
        right: 10%;
        animation-delay: 0.35s;
        --dx: 40px;
        --dy: 35px;
      }
      @keyframes confetti-burst {
        0% {
          opacity: 0;
          transform: translate(0, 0) scale(0.4);
        }
        20% {
          opacity: 1;
        }
        100% {
          opacity: 0;
          transform: translate(var(--dx), var(--dy)) scale(1.1);
        }
      }
      .success h2 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.65rem, 6vw, 2.2rem);
        line-height: 1.05;
        letter-spacing: -0.03em;
        margin: 0 0 0.5rem;
        overflow-wrap: break-word;
      }
      .success h2 .accent {
        background: linear-gradient(120deg, var(--coral), var(--sun-deep));
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }
      .success p {
        font-size: 1rem;
        font-weight: 500;
        color: var(--ink-soft);
        margin: 0 auto 1.75rem;
        max-width: 320px;
      }

      /* === Error / not-found =============================== */
      .info-card {
        padding: 1.25rem 1.4rem;
        background: var(--tint-coral);
        border-radius: 16px;
        display: flex;
        align-items: flex-start;
        gap: 0.85rem;
        margin-bottom: 1rem;
      }
      .info-card-icon {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: var(--coral);
        color: white;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        flex: none;
      }
      .info-card .info-title {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 1.05rem;
        margin: 0 0 0.25rem;
        color: var(--ink);
      }
      .info-card .info-body {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--ink-soft);
        line-height: 1.45;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    const segments = window.location.pathname.split('/').filter(Boolean);
    const idx = segments.indexOf('join');
    if (idx >= 0 && idx + 1 < segments.length) {
      this._code = decodeURIComponent(segments[idx + 1]);
    }
  }
  protected updated() {
    if (!this._hasInitialized && this._code) {
      this._hasInitialized = true;
      void this._initialize();
    }
  }
  private async _initialize() {
    const r = await ApiFactory.anonymous().lookupShopByCode(this._code);
    if (!r.success || !r.data) {
      this._state = 'shop-not-found';
      return;
    }
    this._shopName = r.data.shopName;
    this._shopDisplayName = r.data.shopDisplayName;
    if (!this.userProfile?.isLoggedIn) {
      this._state = 'needs-login';
      return;
    }
    if (!this.userProfile.isCustomer) {
      this._state = 'enrolling';
      const enroll = await ApiFactory.authenticated().enrollAsCustomer();
      if (!enroll.success || !enroll.data) {
        this._error = enroll.error ?? 'Could not enroll as a customer';
        this._state = 'error';
        return;
      }
      const newProfile = new UserProfile({
        ...this.userProfile.profile,
        accessToken: enroll.data,
      });
      window.dispatchEvent(
        new CustomEvent('user-profile-set', { detail: newProfile })
      );
    }
    const memberships =
      await ApiFactory.authenticated().getCustomerMemberships();
    if (memberships.success && memberships.data) {
      const existing = memberships.data.find(
        (m) => m.shopName === this._shopName && m.active
      );
      if (existing) {
        this._state = 'already-member';
        clearPendingJoinCode();
        return;
      }
    }
    this._state = 'ready';
  }
  private async _join() {
    this._state = 'joining';
    const r = await ApiFactory.authenticated().joinShopByCode(this._code);
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Could not join shop';
      this._state = 'error';
      return;
    }
    this._state = 'joined';
    clearPendingJoinCode();
  }
  private _goToLogin() {
    setPendingJoinCode(this._code);
    setUserContextMode('customer');
    router.navigate(resolveRouterPath('login'));
  }
  private _goToCustomerHome() {
    setUserContextMode('customer');
    router.navigate(resolveRouterPath('customer'));
  }

  private _renderHeader() {
    return html`
      <div class="page-brand pa-rise">
        <a class="page-brand-mark" href="/">
          <img src="/assets/icons/pag-asenso-icon.svg" alt="" />
          <span class="page-brand-wordmark">Pag<span>-asenso</span></span>
        </a>
      </div>
    `;
  }
  private _renderHero() {
    return html`
      <div class="hero pa-rise pa-rise-2">
        <span class="pill"
          ><sl-icon name="stars"></sl-icon> Loyalty programme</span
        >
        <h1>
          Join
          <span class="shop-name"
            >${this._shopDisplayName || this._shopName}</span
          >
        </h1>
        <p>Become a member and start earning every time you visit.</p>
      </div>
    `;
  }
  private _renderBenefits() {
    return html`
      <div class="benefits pa-rise pa-rise-3">
        ${DEFAULT_BENEFITS.map(
          (b, i) => html`
            <div
              class="benefit pa-rise"
              style="animation-delay:${(i + 3) * 70}ms"
            >
              <span class="benefit-icon ${b.tone}"
                ><sl-icon name=${b.icon}></sl-icon
              ></span>
              <span class="benefit-label">${b.label}</span>
            </div>
          `
        )}
      </div>
    `;
  }
  private _renderBody() {
    switch (this._state) {
      case 'loading':
        return html`<div class="center">
          <sl-spinner style="font-size:2.5rem;"></sl-spinner>
        </div>`;
      case 'shop-not-found':
        return html`
          <div class="info-card pa-rise pa-rise-2">
            <span class="info-card-icon"
              ><sl-icon name="exclamation-circle"></sl-icon
            ></span>
            <div>
              <div class="info-title">Shop not found</div>
              <p class="info-body">
                No shop matches the code "${this._code}". Check the QR or ask at
                the counter.
              </p>
            </div>
          </div>
        `;
      case 'enrolling':
        return html`
          ${this._renderHero()}
          <div class="center">
            <sl-spinner style="font-size:2rem;"></sl-spinner> Setting up your
            account…
          </div>
        `;
      case 'needs-login':
        return html`
          ${this._renderHero()} ${this._renderBenefits()}
          <div class="actions pa-rise pa-rise-5">
            <button class="pa-btn coral" @click=${this._goToLogin}>
              <sl-icon name="heart-fill"></sl-icon>
              Sign in to join
            </button>
            <p class="action-note">You'll come right back here.</p>
          </div>
        `;
      case 'ready':
        return html`
          ${this._renderHero()} ${this._renderBenefits()}
          <div class="actions pa-rise pa-rise-5">
            <button class="pa-btn coral" @click=${this._join}>
              <sl-icon name="heart-fill"></sl-icon>
              Join ${this._shopDisplayName || this._shopName}
            </button>
          </div>
        `;
      case 'joining':
        return html`
          ${this._renderHero()} ${this._renderBenefits()}
          <div class="actions">
            <button class="pa-btn coral" disabled>
              <sl-spinner style="font-size:16px;"></sl-spinner>
              Joining…
            </button>
          </div>
        `;
      case 'already-member':
        return html`
          <div class="success pa-rise pa-rise-2">
            <div class="success-mark-wrap">
              <div class="success-mark">
                <sl-icon name="check-lg"></sl-icon>
              </div>
            </div>
            <h2>You're already <span class="accent">in</span></h2>
            <p>
              You've already joined ${this._shopDisplayName || this._shopName}'s
              loyalty programme.
            </p>
            <button class="pa-btn coral" @click=${this._goToCustomerHome}>
              <sl-icon name="heart-fill"></sl-icon>
              View my memberships
            </button>
          </div>
        `;
      case 'joined':
        return html`
          <div class="success pa-rise pa-rise-2">
            <div class="success-mark-wrap">
              <div class="confetti">
                <span></span><span></span><span></span><span></span
                ><span></span>
              </div>
              <div class="success-mark">
                <sl-icon name="check-lg"></sl-icon>
              </div>
            </div>
            <h2>
              Welcome to
              <span class="accent"
                >${this._shopDisplayName || this._shopName}</span
              >
            </h2>
            <p>You're now a loyalty member. Enjoy your rewards!</p>
            <button class="pa-btn coral" @click=${this._goToCustomerHome}>
              <sl-icon name="heart-fill"></sl-icon>
              View my memberships
            </button>
          </div>
        `;
      case 'error':
        return html`
          <div class="info-card pa-rise pa-rise-2">
            <span class="info-card-icon"
              ><sl-icon name="exclamation-triangle"></sl-icon
            ></span>
            <div>
              <div class="info-title">Something went wrong</div>
              <p class="info-body">${this._error}</p>
            </div>
          </div>
          <div class="actions">
            <button
              class="pa-btn ghost"
              @click=${() => {
                this._hasInitialized = false;
                this._state = 'loading';
                this._error = null;
                this.requestUpdate();
              }}
            >
              <sl-icon name="arrow-clockwise"></sl-icon>
              Try again
            </button>
          </div>
        `;
    }
  }

  render() {
    return html`
      <div class="page">
        <div class="blob a"></div>
        <div class="blob b"></div>
        <div class="blob c"></div>
        ${this._renderHeader()}
        <div class="stage">${this._renderBody()}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'join-shop-page': JoinShopPage;
  }
}
